import { useEffect } from 'react';
import styled from '@emotion/styled';
import { i18n, mediaQuery, useGetOfferFiles } from 'src/common';
import { colors } from 'src/theme';
import { Flex } from '../grid';
import { Text } from '../typography';
import { LoaderSquare } from '../other';
import { DownloadFileIcon } from '../icons';
import IconModalClose from '../icons/closeModal.icon';

const ModalOverlay = styled.div({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 100,
  backgroundColor: 'rgba(50, 50, 50, 0.55)',
});

const ModalContent = styled.div({
  boxSizing: 'border-box',
  backgroundColor: '#FFF',
  borderRadius: 16,
  maxHeight: '92%',
  width: 'calc(100% - 40px)',
  maxWidth: 560,
  position: 'fixed',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  zIndex: 2,
  padding: 24,
  overflow: 'auto',
  boxShadow: '0 4px 40px rgba(0, 0, 0, 0.06)',
  [mediaQuery('tablet')]: {
    padding: 40,
  },
});

const ModalClose = styled(IconModalClose)({
  position: 'absolute',
  top: 24,
  right: 24,
  cursor: 'pointer',
  [mediaQuery('tablet')]: {
    top: 30,
    right: 30,
  },
});

const ModalTitle = styled.div({
  paddingRight: 34,
  [mediaQuery('tablet')]: {
    paddingRight: 0,
  },
});

const FileRow = styled.a({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '12px 16px',
  borderRadius: 8,
  border: `1px solid ${colors.border5}`,
  textDecoration: 'none',
  cursor: 'pointer',
  transition: 'background 0.15s',
  '&:hover': {
    background: colors.background6,
  },
});

export interface OfferFilesModalProps {
  open: boolean;
  offerId?: string;
  onClose?: () => void;
}

export const OfferFilesModal: React.FC<OfferFilesModalProps> = ({ open, offerId, onClose }) => {
  const { offerFiles, loading, refetch } = useGetOfferFiles(offerId);

  useEffect(() => {
    if (open) {
      refetch();
      const currentOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = currentOverflow;
      };
    }
    return undefined;
  }, [open]);

  if (!open) return null;

  return (
    <ModalOverlay>
      <ModalContent>
        <ModalClose onClick={onClose} />
        <Flex vertical gap={20}>
          <ModalTitle>
            <Text type="large" bold>
              {i18n.t('components.modals.offerFilesModal.title')}
            </Text>
          </ModalTitle>
          {loading && <LoaderSquare overlay={false} />}
          {!loading && offerFiles?.documents && offerFiles.documents.length > 0 && (
            <Flex vertical gap={8}>
              {offerFiles.documents.map((doc) => (
                <FileRow
                  key={doc.fileId}
                  href={doc.downloadLink}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <DownloadFileIcon width={20} height={20} color={colors.primary} />
                  <Text type="base" color={colors.text1}>
                    {doc.fileName}
                  </Text>
                </FileRow>
              ))}
            </Flex>
          )}
          {!loading && (!offerFiles?.documents || offerFiles.documents.length === 0) && (
            <Text type="base" color={colors.text4}>
              {i18n.t('components.modals.offerFilesModal.noFiles')}
            </Text>
          )}
        </Flex>
      </ModalContent>
    </ModalOverlay>
  );
};
